/**
 * useAdminRaffle Hooks
 *
 * URQL hooks for admin raffle management (query + mutations).
 */

import { useCallback } from "react";
import { useQuery, useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// TYPES
// =============================================================================

export type RaffleStatus = "DRAFT" | "READY" | "IN_PROGRESS" | "COMPLETED";

export interface Prize {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
  draw: {
    id: string;
    winningTicket: {
      ticketNumber: string;
      winnerName: string;
      winnerUsername: string | null;
    };
  } | null;
}

export interface Draw {
  id: string;
  prizeId: string;
  prizeName: string;
  winningTicket: {
    ticketNumber: string;
    winnerName: string;
    winnerUsername: string | null;
  };
  drawnAt: string;
}

export interface RaffleTicket {
  id: string;
  ticketNumber: string;
  ticketTypeName: string;
  accountId: string;
  accountName: string;
  accountUsername: string | null;
  isPaid: boolean;
  isUsed: boolean;
}

export type TicketFilter = "ALL" | "PAID" | "USED";

export interface Raffle {
  id: string;
  eventId: string;
  name: string;
  description: string | null;
  status: RaffleStatus;
  isTest: boolean;
  prizes: Prize[];
  ticketCount: number;
  createdAt: string;
  updatedAt: string;
}

interface RaffleInput {
  name: string;
  description?: string | null;
}

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const AdminRaffleEventsDocument = `
  query AdminRaffleEvents {
    adminRaffleEvents {
      id
      title
      coverImage
      startDate
      endDate
      ticketTypesCount
      ticketsCount
      rafflesCount
      activeRafflesCount
    }
  }
`;

const AdminRafflesDocument = `
  query AdminRaffles($eventId: ID!) {
    adminRaffles(eventId: $eventId) {
      id
      eventId
      name
      description
      status
      isTest
      ticketCount
      prizes {
        id
        name
        draw { id }
      }
      createdAt
      updatedAt
    }
  }
`;

const AdminRaffleDocument = `
  query AdminRaffle($id: ID!) {
    adminRaffle(id: $id) {
      id
      eventId
      name
      description
      status
      isTest
      ticketCount
      prizes {
        id
        name
        description
        imageUrl
        sortOrder
        draw {
          id
          winningTicket {
            ticketNumber
            winnerName
            winnerUsername
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

const AdminRaffleTicketsDocument = `
  query AdminRaffleTickets($raffleId: ID!) {
    adminRaffleTickets(raffleId: $raffleId) {
      id
      ticketNumber
      ticketTypeName
      accountId
      accountName
      accountUsername
      isPaid
      isUsed
    }
  }
`;

const AdminEventTicketsDocument = `
  query AdminEventTicketsForRaffle($eventId: ID!, $filter: TicketFilter) {
    adminEventTicketsForRaffle(eventId: $eventId, filter: $filter) {
      id
      ticketNumber
      ticketTypeName
      accountId
      accountName
      accountUsername
      isPaid
      isUsed
    }
  }
`;

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const CreateRaffleMutation = `
  mutation AdminCreateRaffle($eventId: ID!, $input: RaffleInput!) {
    adminCreateRaffle(eventId: $eventId, input: $input) {
      id
      name
      status
    }
  }
`;

const UpdateRaffleMutation = `
  mutation AdminUpdateRaffle($id: ID!, $input: RaffleInput!) {
    adminUpdateRaffle(id: $id, input: $input) {
      id
      name
      description
      isTest
    }
  }
`;

const DeleteRaffleMutation = `
  mutation AdminDeleteRaffle($id: ID!) {
    adminDeleteRaffle(id: $id)
  }
`;

const CopyRaffleMutation = `
  mutation AdminCopyRaffle($id: ID!, $isTest: Boolean) {
    adminCopyRaffle(id: $id, isTest: $isTest) {
      id
      name
      status
      isTest
    }
  }
`;

const ResetTestRaffleMutation = `
  mutation AdminResetTestRaffle($raffleId: ID!) {
    adminResetTestRaffle(raffleId: $raffleId) {
      id
      name
      status
      isTest
    }
  }
`;

const AddPrizeMutation = `
  mutation AdminAddPrize($raffleId: ID!, $input: PrizeInput!) {
    adminAddPrize(raffleId: $raffleId, input: $input) {
      id
      name
      sortOrder
    }
  }
`;

const UpdatePrizeMutation = `
  mutation AdminUpdatePrize($id: ID!, $input: PrizeInput!) {
    adminUpdatePrize(id: $id, input: $input) {
      id
      name
      description
      imageUrl
    }
  }
`;

const DeletePrizeMutation = `
  mutation AdminDeletePrize($id: ID!) {
    adminDeletePrize(id: $id)
  }
`;

const ReorderPrizesMutation = `
  mutation AdminReorderPrizes($raffleId: ID!, $prizeIds: [ID!]!) {
    adminReorderPrizes(raffleId: $raffleId, prizeIds: $prizeIds) {
      id
      sortOrder
    }
  }
`;

const SetRaffleTicketsMutation = `
  mutation AdminSetRaffleTickets($raffleId: ID!, $ticketIds: [ID!]!) {
    adminSetRaffleTickets(raffleId: $raffleId, ticketIds: $ticketIds)
  }
`;

const AddAllEventTicketsMutation = `
  mutation AdminAddAllEventTickets($raffleId: ID!) {
    adminAddAllEventTickets(raffleId: $raffleId)
  }
`;

const ReadyRaffleMutation = `
  mutation AdminReadyRaffle($id: ID!) {
    adminReadyRaffle(id: $id) { id status }
  }
`;

const UnreadyRaffleMutation = `
  mutation AdminUnreadyRaffle($id: ID!) {
    adminUnreadyRaffle(id: $id) { id status }
  }
`;

const StartRaffleMutation = `
  mutation AdminStartRaffle($id: ID!) {
    adminStartRaffle(id: $id) { id status }
  }
`;

const CompleteRaffleMutation = `
  mutation AdminCompleteRaffle($id: ID!) {
    adminCompleteRaffle(id: $id) { id status }
  }
`;

const DrawPrizeMutation = `
  mutation AdminDrawPrize($raffleId: ID!, $prizeId: ID!) {
    adminDrawPrize(raffleId: $raffleId, prizeId: $prizeId) {
      id
      prizeId
      winningTicket {
        ticketNumber
        winnerName
        winnerUsername
      }
    }
  }
`;

const NextPrizeMutation = `
  mutation AdminNextPrize($raffleId: ID!) {
    adminNextPrize(raffleId: $raffleId)
  }
`;

const ShowWinnerMutation = `
  mutation AdminShowWinner($raffleId: ID!, $prizeId: ID!) {
    adminShowWinner(raffleId: $raffleId, prizeId: $prizeId)
  }
`;

// =============================================================================
// QUERY HOOKS
// =============================================================================

// --- Raffle Events (for raffle operator section) ---

export interface RaffleEventSummary {
  id: string;
  title: string;
  coverImage: string | null;
  startDate: string | null;
  endDate: string | null;
  ticketTypesCount: number;
  ticketsCount: number;
  rafflesCount: number;
  activeRafflesCount: number;
}

interface UseAdminRaffleEventsResult {
  events: RaffleEventSummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAdminRaffleEvents(): UseAdminRaffleEventsResult {
  const [result, reexecuteQuery] = useQuery<{ adminRaffleEvents: RaffleEventSummary[] }>({
    query: AdminRaffleEventsDocument,
  });

  return {
    events: result.data?.adminRaffleEvents ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

// --- Raffles List ---

interface UseAdminRafflesResult {
  raffles: Raffle[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAdminRaffles(eventId: string | undefined): UseAdminRafflesResult {
  const [result, reexecuteQuery] = useQuery<{ adminRaffles: Raffle[] }>({
    query: AdminRafflesDocument,
    variables: { eventId },
    pause: !eventId,
    requestPolicy: "cache-and-network", // Always fetch fresh data
  });

  return {
    raffles: result.data?.adminRaffles ?? [],
    isLoading: result.fetching,
    error: result.error ? extractGraphQLErrorMessage(result.error, "Ошибка загрузки розыгрышей") : null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

interface UseAdminRaffleResult {
  raffle: Raffle | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAdminRaffle(raffleId: string | undefined): UseAdminRaffleResult {
  const [result, reexecuteQuery] = useQuery<{ adminRaffle: Raffle | null }>({
    query: AdminRaffleDocument,
    variables: { id: raffleId },
    pause: !raffleId,
  });

  return {
    raffle: result.data?.adminRaffle ?? null,
    isLoading: result.fetching,
    error: result.error ? extractGraphQLErrorMessage(result.error, "Ошибка загрузки розыгрыша") : null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

interface UseAdminRaffleTicketsResult {
  tickets: RaffleTicket[];
  eventTickets: RaffleTicket[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAdminRaffleTickets(
  raffleId: string | undefined,
  eventId: string | undefined,
  filter: TicketFilter = "ALL"
): UseAdminRaffleTicketsResult {
  const [ticketsResult, reexecuteTickets] = useQuery<{ adminRaffleTickets: RaffleTicket[] }>({
    query: AdminRaffleTicketsDocument,
    variables: { raffleId },
    pause: !raffleId,
  });

  const [eventTicketsResult, reexecuteEventTickets] = useQuery<{ adminEventTicketsForRaffle: RaffleTicket[] }>({
    query: AdminEventTicketsDocument,
    variables: { eventId, filter },
    pause: !eventId,
  });

  return {
    tickets: ticketsResult.data?.adminRaffleTickets ?? [],
    eventTickets: eventTicketsResult.data?.adminEventTicketsForRaffle ?? [],
    isLoading: ticketsResult.fetching || eventTicketsResult.fetching,
    error: ticketsResult.error 
      ? extractGraphQLErrorMessage(ticketsResult.error, "Ошибка загрузки билетов") 
      : eventTicketsResult.error 
        ? extractGraphQLErrorMessage(eventTicketsResult.error, "Ошибка загрузки билетов события") 
        : null,
    refetch: () => {
      reexecuteTickets({ requestPolicy: "network-only" });
      reexecuteEventTickets({ requestPolicy: "network-only" });
    },
  };
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

interface UseCreateRaffleResult {
  createRaffle: (eventId: string, input: RaffleInput) => Promise<string>;
  isLoading: boolean;
}

export function useCreateRaffle(): UseCreateRaffleResult {
  const [result, executeMutation] = useMutation<{ adminCreateRaffle: { id: string } }>(
    CreateRaffleMutation
  );

  const createRaffle = useCallback(
    async (eventId: string, input: RaffleInput): Promise<string> => {
      const response = await executeMutation({ eventId, input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка создания розыгрыша");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "createRaffle" },
        });
        throw err;
      }
      return response.data!.adminCreateRaffle.id;
    },
    [executeMutation]
  );

  return { createRaffle, isLoading: result.fetching };
}

interface UseUpdateRaffleResult {
  updateRaffle: (args: { id: string; name: string; description?: string; isTest?: boolean }) => Promise<void>;
  isLoading: boolean;
}

export function useUpdateRaffle(): UseUpdateRaffleResult {
  const [result, executeMutation] = useMutation<{ adminUpdateRaffle: { id: string } }>(
    UpdateRaffleMutation
  );

  const updateRaffle = useCallback(
    async (args: { id: string; name: string; description?: string; isTest?: boolean }): Promise<void> => {
      const response = await executeMutation({ 
        id: args.id, 
        input: { name: args.name, description: args.description, isTest: args.isTest } 
      });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления розыгрыша");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "updateRaffle" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { updateRaffle, isLoading: result.fetching };
}

interface UseDeleteRaffleResult {
  deleteRaffle: (args: { id: string; eventId: string }) => Promise<void>;
  isLoading: boolean;
}

export function useDeleteRaffle(): UseDeleteRaffleResult {
  const [result, executeMutation] = useMutation<{ adminDeleteRaffle: boolean }>(
    DeleteRaffleMutation
  );

  const deleteRaffle = useCallback(
    async (args: { id: string; eventId: string }): Promise<void> => {
      const response = await executeMutation({ id: args.id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления розыгрыша");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "deleteRaffle" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { deleteRaffle, isLoading: result.fetching };
}

interface UseCopyRaffleResult {
  copyRaffle: (args: { id: string; isTest?: boolean }) => Promise<string>;
  isLoading: boolean;
}

export function useCopyRaffle(): UseCopyRaffleResult {
  const [result, executeMutation] = useMutation<{ adminCopyRaffle: { id: string } }>(
    CopyRaffleMutation
  );

  const copyRaffle = useCallback(
    async (args: { id: string; isTest?: boolean }): Promise<string> => {
      const response = await executeMutation({ id: args.id, isTest: args.isTest });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка копирования розыгрыша");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "copyRaffle" },
        });
        throw err;
      }
      return response.data!.adminCopyRaffle.id;
    },
    [executeMutation]
  );

  return { copyRaffle, isLoading: result.fetching };
}

// -----------------------------------------------------------------------------
// useResetTestRaffle
// -----------------------------------------------------------------------------

interface UseResetTestRaffleResult {
  resetTestRaffle: (raffleId: string) => Promise<void>;
  isLoading: boolean;
}

export function useResetTestRaffle(): UseResetTestRaffleResult {
  const [result, executeMutation] = useMutation<{ adminResetTestRaffle: { id: string } }>(
    ResetTestRaffleMutation
  );

  const resetTestRaffle = useCallback(
    async (raffleId: string): Promise<void> => {
      const response = await executeMutation({ raffleId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка сброса тестового розыгрыша");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "resetTestRaffle" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { resetTestRaffle, isLoading: result.fetching };
}

// =============================================================================
// PRIZE MUTATION HOOKS
// =============================================================================

interface UseRafflePrizesResult {
  addPrize: (args: { raffleId: string; name: string; description?: string; imageUrl?: string }) => Promise<string>;
  updatePrize: (args: { id: string; name: string; description?: string; imageUrl?: string }) => Promise<void>;
  deletePrize: (args: { id: string; raffleId: string }) => Promise<void>;
  reorderPrizes: (args: { raffleId: string; prizeIds: string[] }) => Promise<void>;
  isLoading: boolean;
}

export function useRafflePrizes(): UseRafflePrizesResult {
  const [addResult, executeAdd] = useMutation<{ adminAddPrize: { id: string } }>(AddPrizeMutation);
  const [updateResult, executeUpdate] = useMutation<{ adminUpdatePrize: { id: string } }>(UpdatePrizeMutation);
  const [deleteResult, executeDelete] = useMutation<{ adminDeletePrize: boolean }>(DeletePrizeMutation);
  const [reorderResult, executeReorder] = useMutation<{ adminReorderPrizes: { id: string; sortOrder: number }[] }>(ReorderPrizesMutation);

  const addPrize = useCallback(
    async (args: { raffleId: string; name: string; description?: string; imageUrl?: string }): Promise<string> => {
      const response = await executeAdd({
        raffleId: args.raffleId,
        input: { name: args.name, description: args.description, imageUrl: args.imageUrl },
      });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка добавления приза");
        throw new Error(message);
      }
      return response.data!.adminAddPrize.id;
    },
    [executeAdd]
  );

  const updatePrize = useCallback(
    async (args: { id: string; name: string; description?: string; imageUrl?: string }): Promise<void> => {
      const response = await executeUpdate({
        id: args.id,
        input: { name: args.name, description: args.description, imageUrl: args.imageUrl },
      });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления приза");
        throw new Error(message);
      }
    },
    [executeUpdate]
  );

  const deletePrize = useCallback(
    async (args: { id: string; raffleId: string }): Promise<void> => {
      const response = await executeDelete({ id: args.id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления приза");
        throw new Error(message);
      }
    },
    [executeDelete]
  );

  const reorderPrizes = useCallback(
    async (args: { raffleId: string; prizeIds: string[] }): Promise<void> => {
      const response = await executeReorder(args);
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка сортировки призов");
        throw new Error(message);
      }
    },
    [executeReorder]
  );

  return {
    addPrize,
    updatePrize,
    deletePrize,
    reorderPrizes,
    isLoading: addResult.fetching || updateResult.fetching || deleteResult.fetching || reorderResult.fetching,
  };
}

// =============================================================================
// TICKET MUTATION HOOKS
// =============================================================================

interface UseRaffleTicketsResult {
  setTickets: (args: { raffleId: string; ticketIds: string[] }) => Promise<void>;
  addAllTickets: (args: { raffleId: string }) => Promise<number>;
  isLoading: boolean;
}

export function useRaffleTickets(): UseRaffleTicketsResult {
  const [setResult, executeSet] = useMutation<{ adminSetRaffleTickets: number }>(SetRaffleTicketsMutation);
  const [addAllResult, executeAddAll] = useMutation<{ adminAddAllEventTickets: number }>(AddAllEventTicketsMutation);

  const setTickets = useCallback(
    async (args: { raffleId: string; ticketIds: string[] }): Promise<void> => {
      const response = await executeSet(args);
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления билетов");
        throw new Error(message);
      }
    },
    [executeSet]
  );

  const addAllTickets = useCallback(
    async (args: { raffleId: string }): Promise<number> => {
      const response = await executeAddAll(args);
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка добавления всех билетов");
        throw new Error(message);
      }
      return response.data?.adminAddAllEventTickets ?? 0;
    },
    [executeAddAll]
  );

  return {
    setTickets,
    addAllTickets,
    isLoading: setResult.fetching || addAllResult.fetching,
  };
}

// =============================================================================
// STATE MUTATION HOOKS
// =============================================================================

interface UseRaffleStateMutationsResult {
  readyRaffle: (id: string) => Promise<void>;
  unreadyRaffle: (id: string) => Promise<void>;
  startRaffle: (id: string) => Promise<void>;
  completeRaffle: (id: string) => Promise<void>;
  isLoading: boolean;
}

export function useRaffleStateMutations(): UseRaffleStateMutationsResult {
  const [readyResult, executeReady] = useMutation<{ adminReadyRaffle: { id: string } }>(ReadyRaffleMutation);
  const [unreadyResult, executeUnready] = useMutation<{ adminUnreadyRaffle: { id: string } }>(UnreadyRaffleMutation);
  const [startResult, executeStart] = useMutation<{ adminStartRaffle: { id: string } }>(StartRaffleMutation);
  const [completeResult, executeComplete] = useMutation<{ adminCompleteRaffle: { id: string } }>(CompleteRaffleMutation);

  const readyRaffle = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeReady({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка подготовки розыгрыша");
        throw new Error(message);
      }
    },
    [executeReady]
  );

  const unreadyRaffle = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeUnready({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка отмены подготовки");
        throw new Error(message);
      }
    },
    [executeUnready]
  );

  const startRaffle = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeStart({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка запуска розыгрыша");
        throw new Error(message);
      }
    },
    [executeStart]
  );

  const completeRaffle = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeComplete({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка завершения розыгрыша");
        throw new Error(message);
      }
    },
    [executeComplete]
  );

  return {
    readyRaffle,
    unreadyRaffle,
    startRaffle,
    completeRaffle,
    isLoading: readyResult.fetching || unreadyResult.fetching || startResult.fetching || completeResult.fetching,
  };
}

// =============================================================================
// DRAW MUTATION HOOK
// =============================================================================

interface DrawResult {
  id: string;
  prizeId: string;
  winningTicket: {
    ticketNumber: string;
    winnerName: string;
    winnerUsername: string | null;
  };
}

interface UseDrawPrizeResult {
  drawPrize: (raffleId: string, prizeId: string) => Promise<DrawResult>;
  isLoading: boolean;
}

export function useDrawPrize(): UseDrawPrizeResult {
  const [result, executeMutation] = useMutation<{ adminDrawPrize: DrawResult }>(DrawPrizeMutation);

  const drawPrize = useCallback(
    async (raffleId: string, prizeId: string): Promise<DrawResult> => {
      const response = await executeMutation({ raffleId, prizeId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка розыгрыша приза");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "drawPrize" },
        });
        throw err;
      }
      return response.data!.adminDrawPrize;
    },
    [executeMutation]
  );

  return { drawPrize, isLoading: result.fetching };
}

// =============================================================================
// NEXT PRIZE (transition to next prize after showing winner)
// =============================================================================

interface UseNextPrizeResult {
  nextPrize: (raffleId: string) => Promise<boolean>;
  isLoading: boolean;
}

export function useNextPrize(): UseNextPrizeResult {
  const [result, executeMutation] = useMutation<{ adminNextPrize: boolean }>(NextPrizeMutation);

  const nextPrize = useCallback(
    async (raffleId: string): Promise<boolean> => {
      const response = await executeMutation({ raffleId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка перехода к следующему призу");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "nextPrize" },
        });
        throw err;
      }
      return response.data!.adminNextPrize;
    },
    [executeMutation]
  );

  return { nextPrize, isLoading: result.fetching };
}

// === Show Winner ===

interface UseShowWinnerResult {
  showWinner: (raffleId: string, prizeId: string) => Promise<boolean>;
  isLoading: boolean;
}

export function useShowWinner(): UseShowWinnerResult {
  const [result, executeMutation] = useMutation<{ adminShowWinner: boolean }>(ShowWinnerMutation);

  const showWinner = useCallback(
    async (raffleId: string, prizeId: string): Promise<boolean> => {
      const response = await executeMutation({ raffleId, prizeId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка показа победителя");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "showWinner" },
        });
        throw err;
      }
      return response.data!.adminShowWinner;
    },
    [executeMutation]
  );

  return { showWinner, isLoading: result.fetching };
}

// === Show Completed ===

const ShowCompletedMutation = `
  mutation AdminShowCompleted($raffleId: ID!) {
    adminShowCompleted(raffleId: $raffleId)
  }
`;

interface UseShowCompletedResult {
  showCompleted: (raffleId: string) => Promise<boolean>;
  isLoading: boolean;
}

export function useShowCompleted(): UseShowCompletedResult {
  const [result, executeMutation] = useMutation<{ adminShowCompleted: boolean }>(ShowCompletedMutation);

  const showCompleted = useCallback(
    async (raffleId: string): Promise<boolean> => {
      const response = await executeMutation({ raffleId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка показа финального экрана");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-raffle", action: "showCompleted" },
        });
        throw err;
      }
      return response.data!.adminShowCompleted;
    },
    [executeMutation]
  );

  return { showCompleted, isLoading: result.fetching };
}
